import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { quizAPI } from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription} from '../../components/ui/dialog';
import {
  ArrowLeft, Plus, Trash2, ClipboardCheck, CheckCircle, XCircle,
  Users, Loader2, GripVertical
} from 'lucide-react';
import { formatFullDateTime12h } from '../../utils/timeFormat';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const QuizManagement = () => {
  const navigate = useNavigate();
  const [quizzes, setQuizzes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [showResults, setShowResults] = useState(null);
  const [results, setResults] = useState([]);
  const [resultsLoading, setResultsLoading] = useState(false);

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const dashPath = user.role === 'client' ? '/client/dashboard' : '/admin/dashboard';

  const [form, setForm] = useState({
    title: '', description: '', passing_score: 80, is_required: true,
    max_retakes: -1, time_limit_minutes: null,
    questions: [{ question_text: '', question_type: 'multiple_choice', options: ['', '', '', ''], correct_answer: '0', points: 1 }],
  });

  useEffect(() => { fetchQuizzes(); }, []);

  const fetchQuizzes = async () => {
    try {
      const res = await quizAPI.list();
      setQuizzes(res.data);
    } catch { toast.error('Failed to load quizzes'); }
    finally { setLoading(false); }
  };

  const addQuestion = () => {
    setForm(f => ({
      ...f,
      questions: [...f.questions, { question_text: '', question_type: 'multiple_choice', options: ['', '', '', ''], correct_answer: '0', points: 1 }]
    }));
  };

  const removeQuestion = (idx) => {
    if (form.questions.length <= 1) return;
    setForm(f => ({ ...f, questions: f.questions.filter((_, i) => i !== idx) }));
  };

  const updateQuestion = (idx, field, value) => {
    setForm(f => {
      const qs = [...f.questions];
      qs[idx] = { ...qs[idx], [field]: value };
      if (field === 'question_type' && value === 'true_false') {
        qs[idx].options = ['True', 'False'];
        qs[idx].correct_answer = '0';
      } else if (field === 'question_type' && value === 'multiple_choice') {
        qs[idx].options = ['', '', '', ''];
        qs[idx].correct_answer = '0';
      }
      return { ...f, questions: qs };
    });
  };

  const updateOption = (qIdx, optIdx, val) => {
    setForm(f => {
      const qs = [...f.questions];
      const opts = [...qs[qIdx].options];
      opts[optIdx] = val;
      qs[qIdx] = { ...qs[qIdx], options: opts };
      return { ...f, questions: qs };
    });
  };

  const handleCreate = async () => {
    if (!form.title.trim()) { toast.error('Title required'); return; }
    const hasEmpty = form.questions.some(q => !q.question_text.trim() || q.options.some(o => !o.trim()));
    if (hasEmpty) { toast.error('Fill in all questions and options'); return; }
    setCreating(true);
    try {
      await quizAPI.create(form);
      toast.success('Quiz created!');
      setShowCreate(false);
      setForm({ title: '', description: '', passing_score: 80, is_required: true, max_retakes: -1, time_limit_minutes: null, questions: [{ question_text: '', question_type: 'multiple_choice', options: ['', '', '', ''], correct_answer: '0', points: 1 }] });
      fetchQuizzes();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to create quiz'); }
    finally { setCreating(false); }
  };

  const viewResults = async (quizId) => {
    setShowResults(quizId);
    setResultsLoading(true);
    try {
      const res = await quizAPI.results({ quiz_id: quizId });
      setResults(res.data);
    } catch { toast.error('Failed to load results'); }
    finally { setResultsLoading(false); }
  };

  const deleteQuiz = async (id) => {
    try {
      await quizAPI.delete(id);
      toast.success('Quiz deleted');
      fetchQuizzes();
    } catch { toast.error('Failed to delete'); }
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="quiz-management-page">
      <header className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" onClick={() => navigate(dashPath)} data-testid="back-button">
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <ClipboardCheck className="w-5 h-5 text-cyan-600" />
            <h1 className="text-lg font-semibold text-gray-900">Quiz Management</h1>
          </div>
          <Button onClick={() => setShowCreate(true)} className="bg-cyan-500 hover:bg-cyan-600" data-testid="create-quiz-btn">
            <Plus className="w-4 h-4 mr-2" />New Quiz
          </Button>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 py-8">
        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-cyan-500" /></div>
        ) : quizzes.length === 0 ? (
          <Card><CardContent className="py-12 text-center">
            <ClipboardCheck className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 mb-2">No quizzes yet</p>
            <p className="text-sm text-gray-400">Create quizzes to assess talent before booking.</p>
          </CardContent></Card>
        ) : (
          <div className="grid gap-4">
            {quizzes.map(q => (
              <Card key={q.id} className="bg-white" data-testid="quiz-card">
                <CardContent className="p-5">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="font-semibold text-gray-900">{q.title}</h3>
                        {q.is_required && <Badge className="bg-red-100 text-red-700 text-[10px]">Required</Badge>}
                      </div>
                      {q.description && <p className="text-sm text-gray-500 mb-3">{q.description}</p>}
                      <div className="flex flex-wrap gap-4 text-xs text-gray-500">
                        <span>{q.question_count} questions</span>
                        <span>Pass: {q.passing_score}%</span>
                        <span>{q.attempt_count} attempts</span>
                        <span className="text-green-600">{q.pass_count} passed</span>
                        {q.max_retakes > 0 && <span>Max retakes: {q.max_retakes}</span>}
                        {q.time_limit_minutes && <span>{q.time_limit_minutes} min limit</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button variant="outline" size="sm" onClick={() => viewResults(q.id)} data-testid="view-results-btn">
                        <Users className="w-4 h-4 mr-1" />Results
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => deleteQuiz(q.id)} className="text-red-500 hover:text-red-700" data-testid="delete-quiz-btn">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Create Quiz Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto bg-white" data-testid="create-quiz-dialog">
          <DialogHeader>
            <DialogTitle>Create New Quiz</DialogTitle>
          <DialogDescription className="sr-only">Create New Quiz</DialogDescription>
          </DialogHeader>
          <div className="space-y-6 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2 col-span-2">
                <Label>Title *</Label>
                <Input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="e.g. Bartender Service Standards" data-testid="quiz-title-input" />
              </div>
              <div className="space-y-2 col-span-2">
                <Label>Description</Label>
                <Textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="What this quiz covers..." rows={2} />
              </div>
              <div className="space-y-2">
                <Label>Passing Score (%)</Label>
                <Input type="number" min={1} max={100} value={form.passing_score} onChange={e => setForm(f => ({ ...f, passing_score: parseInt(e.target.value) || 80 }))} data-testid="passing-score-input" />
              </div>
              <div className="space-y-2">
                <Label>Max Retakes (-1 = unlimited)</Label>
                <Input type="number" min={-1} value={form.max_retakes} onChange={e => setForm(f => ({ ...f, max_retakes: parseInt(e.target.value) ?? -1 }))} />
              </div>
            </div>

            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-medium text-gray-900">Questions</h3>
                <Button type="button" variant="outline" size="sm" onClick={addQuestion} data-testid="add-question-btn">
                  <Plus className="w-4 h-4 mr-1" />Add Question
                </Button>
              </div>

              {form.questions.map((q, qi) => (
                <Card key={qi} className="border-gray-200" data-testid="question-card">
                  <CardContent className="p-4 space-y-3">
                    <div className="flex items-start gap-3">
                      <GripVertical className="w-4 h-4 text-gray-300 mt-2.5 shrink-0" />
                      <div className="flex-1 space-y-3">
                        <div className="flex gap-3">
                          <div className="flex-1">
                            <Input value={q.question_text} onChange={e => updateQuestion(qi, 'question_text', e.target.value)} placeholder={`Question ${qi + 1}`} data-testid="question-text-input" />
                          </div>
                          <Select value={q.question_type} onValueChange={v => updateQuestion(qi, 'question_type', v)}>
                            <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
                            <SelectContent className="bg-white">
                              <SelectItem value="multiple_choice">Multiple Choice</SelectItem>
                              <SelectItem value="true_false">True / False</SelectItem>
                            </SelectContent>
                          </Select>
                          {form.questions.length > 1 && (
                            <Button variant="ghost" size="sm" onClick={() => removeQuestion(qi)} className="text-red-400 shrink-0">
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>

                        <div className="space-y-2">
                          {q.options.map((opt, oi) => (
                            <div key={oi} className="flex items-center gap-2">
                              <button
                                type="button"
                                onClick={() => updateQuestion(qi, 'correct_answer', String(oi))}
                                className={`w-6 h-6 rounded-full border-2 flex items-center justify-center shrink-0 transition-colors ${
                                  q.correct_answer === String(oi) ? 'border-green-500 bg-green-500 text-white' : 'border-gray-300'
                                }`}
                                data-testid={`correct-answer-${qi}-${oi}`}
                              >
                                {q.correct_answer === String(oi) && <CheckCircle className="w-3.5 h-3.5" />}
                              </button>
                              {q.question_type === 'true_false' ? (
                                <span className="text-sm text-gray-700">{opt}</span>
                              ) : (
                                <Input
                                  value={opt}
                                  onChange={e => updateOption(qi, oi, e.target.value)}
                                  placeholder={`Option ${String.fromCharCode(65 + oi)}`}
                                  className="flex-1"
                                  data-testid={`option-input-${qi}-${oi}`}
                                />
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button onClick={handleCreate} disabled={creating} className="bg-cyan-500 hover:bg-cyan-600" data-testid="save-quiz-btn">
              {creating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}Create Quiz
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Results Dialog */}
      <Dialog open={!!showResults} onOpenChange={() => setShowResults(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto bg-white" data-testid="results-dialog">
          <DialogHeader>
            <DialogTitle>Quiz Results</DialogTitle>
          <DialogDescription className="sr-only">Quiz Results</DialogDescription>
          </DialogHeader>
          {resultsLoading ? (
            <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-cyan-500" /></div>
          ) : results.length === 0 ? (
            <p className="text-center py-8 text-gray-400">No attempts yet</p>
          ) : (
            <div className="space-y-3">
              {results.map(r => (
                <div key={r.attempt_id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg" data-testid="result-row">
                  <div>
                    <p className="font-medium text-gray-900">{r.talent_name}</p>
                    <p className="text-xs text-gray-500">{r.role_name} | Attempt #{r.attempt_number}</p>
                  </div>
                  <div className="text-right">
                    <div className="flex items-center gap-2">
                      <span className={`text-lg font-bold ${r.passed ? 'text-green-600' : 'text-red-500'}`}>{r.percentage}%</span>
                      {r.passed ? <CheckCircle className="w-5 h-5 text-green-500" /> : <XCircle className="w-5 h-5 text-red-400" />}
                    </div>
                    <p className="text-xs text-gray-400">{r.completed_at ? formatFullDateTime12h(r.completed_at) : 'In progress'}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default QuizManagement;
